import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Popconfirm,
  message,
  Row,
  Col,
  Typography,
  List,
  Descriptions,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { knowledgeApi } from '../../services/knowledge';
import type { ProposalTemplate } from '../../types/knowledge';
import { COLORS } from '../../constants/colors';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;

const TEMPLATE_SECTIONS = [
  { key: 'executive_summary', label: '执行摘要' },
  { key: 'influencer_list', label: '红人列表' },
  { key: 'pricing_analysis', label: '报价分析' },
  { key: 'content_strategy', label: '内容策略' },
  { key: 'timeline', label: '时间规划' },
  { key: 'budget', label: '预算明细' },
  { key: 'kpi', label: 'KPI指标' },
  { key: 'case_studies', label: '案例展示' },
  { key: 'appendix', label: '附录' },
];

const FORMAT_MAP: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
  excel: { color: 'green', text: 'Excel', icon: <FileExcelOutlined /> },
  ppt: { color: 'orange', text: 'PPT', icon: <FileTextOutlined /> },
};

export const ProposalTemplates: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProposalTemplate[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProposalTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<ProposalTemplate | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await knowledgeApi.getProposalTemplates();
      // 后端返回格式: { data: T[], pagination: { total, page, page_size, total_pages } }
      setData(response.data || []);
    } catch {
      // 错误已在request拦截器中处理
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const handleCreate = () => {
    setEditingTemplate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: ProposalTemplate) => {
    setEditingTemplate(record);
    const contentStructure = record.content_structure as { sections?: string[] } | undefined;
    form.setFieldsValue({
      ...record,
      sections: contentStructure?.sections || [],
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await knowledgeApi.deleteProposalTemplate(id);
      message.success('删除成功');
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[ProposalTemplates] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const handlePreview = (record: ProposalTemplate) => {
    setPreviewTemplate(record);
    setPreviewVisible(true);
  };

  const handleSubmit = async (values: { name: string; description: string; format: 'excel' | 'ppt'; sections: string[] }) => {
    try {
      const submitData = {
        name: values.name,
        description: values.description,
        format: values.format,
        content_structure: { sections: values.sections },
      };
      if (editingTemplate) {
        await knowledgeApi.updateProposalTemplate(editingTemplate.id, submitData);
        message.success('更新成功');
      } else {
        await knowledgeApi.createProposalTemplate(submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[ProposalTemplates] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const filteredData = data.filter((item) => {
    return !searchKeyword || 
      item.name.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      (item.description?.toLowerCase().includes(searchKeyword.toLowerCase()));
  });

  const columns = [
    {
      title: '模板名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: ProposalTemplate) => (
        <Space>
          {record.format && FORMAT_MAP[record.format]?.icon}
          <Text strong>{text}</Text>
          {record.is_default && <Tag color="gold">默认</Tag>}
        </Space>
      ),
    },
    {
      title: '格式',
      dataIndex: 'format',
      key: 'format',
      width: 100,
      render: (format: string) => {
        const config = FORMAT_MAP[format] || { color: 'default', text: format };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '包含章节',
      dataIndex: 'content_structure',
      key: 'content_structure',
      width: 300,
      render: (contentStructure: Record<string, unknown>) => {
        const sections = (contentStructure?.sections as string[]) || [];
        return (
          <Space size={[0, 4]} wrap>
            {sections.slice(0, 4).map((section: string) => {
              const sectionInfo = TEMPLATE_SECTIONS.find((s) => s.key === section);
              return (
                <Tag key={section} style={{ marginBottom: 4 }}>
                  {sectionInfo?.label || section}
                </Tag>
              );
            })}
            {sections.length > 4 && (
              <Tag>+{sections.length - 4}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (text: string) => text?.split('T')[0] || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right' as const,
      render: (_: unknown, record: ProposalTemplate) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          >
            预览
          </Button>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此模板？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索和操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="搜索模板名称或描述"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新建模板
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
      />

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingTemplate ? '编辑提案模板' : '新建提案模板'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="name"
                label="模板名称"
                rules={[{ required: true, message: '请输入模板名称' }]}
              >
                <Input placeholder="请输入模板名称" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="format"
                label="导出格式"
                rules={[{ required: true, message: '请选择导出格式' }]}
              >
                <Select placeholder="选择格式">
                  <Option value="excel">Excel</Option>
                  <Option value="ppt">PPT</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="模板描述">
            <TextArea rows={3} placeholder="输入模板描述（可选）" />
          </Form.Item>
          <Form.Item
            name="sections"
            label="包含章节"
            rules={[{ required: true, message: '请至少选择一个章节' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择提案包含的章节"
              optionLabelProp="label"
            >
              {TEMPLATE_SECTIONS.map((section) => (
                <Option key={section.key} value={section.key} label={section.label}>
                  <Space>
                    <AppstoreOutlined />
                    {section.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 预览弹窗 */}
      <Modal
        title="模板预览"
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewTemplate(null);
        }}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {previewTemplate && (
          <div>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="模板名称" span={2}>
                <Text strong>{previewTemplate.name}</Text>
                {previewTemplate.is_default && (
                  <Tag color="gold" style={{ marginLeft: 8 }}>默认</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="导出格式">
                <Tag color={FORMAT_MAP[previewTemplate.format]?.color}>
                  {FORMAT_MAP[previewTemplate.format]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {previewTemplate.created_at?.split('T')[0]}
              </Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                {previewTemplate.description || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
              包含章节
            </Title>
            <List
              grid={{ column: 2 }}
              dataSource={(() => {
                const contentStructure = previewTemplate.content_structure as { sections?: string[] } | undefined;
                const sections = contentStructure?.sections || [];
                return sections.map((s: string) => {
                  const sectionInfo = TEMPLATE_SECTIONS.find((ts) => ts.key === s);
                  return {
                    key: s,
                    label: sectionInfo?.label || s,
                  };
                });
              })()}
              renderItem={(item: { key: string; label: string }) => (
                <List.Item>
                  <Card size="small">
                    <Space>
                      <AppstoreOutlined style={{ color: COLORS.primary }} />
                      <Text>{item.label}</Text>
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
